import { c as _c } from "react/compiler-runtime";
import { useAppearance } from '@onefootprint/appearance';
import { getSessionId } from '@onefootprint/dev-tools';
import { hostedOnboardingD2pGenerateOptions } from '@onefootprint/fetch-hosted';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import useD2pStatusPoll, { isTerminalStatus } from '@/idv2/hooks/use-d2p-status-poll';
import { type DeviceInfo, isDesktopDevice } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'use-d2p-session'
});
export { isTerminalStatus };
type UseD2pSessionParams = {
  authToken: string;
  device: DeviceInfo;
  l10n?: {
    language?: string;
    locale?: string;
  };
  onCancel?: () => void;
  onFinish?: () => void;
  sandboxIdDocOutcome?: string;
};

// Manages a D2P session: generating scoped tokens, polling for status updates, and canceling sessions
const useD2pSession = t0 => {
  const $ = _c(34);
  const {
    authToken,
    device,
    l10n,
    onCancel,
    onFinish,
    sandboxIdDocOutcome
  } = t0;
  const queryClient = useQueryClient();
  const appearance = useAppearance();
  let t1;
  if ($[0] !== appearance || $[1] !== authToken || $[2] !== device || $[3] !== l10n || $[4] !== sandboxIdDocOutcome) {
    const styleParams = appearance ? JSON.stringify(appearance) : undefined;
    const sessionId = getSessionId();
    const isMobile = !isDesktopDevice(device);
    const redirectUrl = isMobile ? window.location.href : undefined;
    t1 = hostedOnboardingD2pGenerateOptions({
      headers: {
        "X-Fp-Authorization": authToken
      },
      body: {
        meta: {
          opener: device.type,
          sessionId: sessionId ?? undefined,
          styleParams,
          sandboxIdDocOutcome,
          redirectUrl,
          l10n: l10n ? {
            language: l10n.language,
            locale: l10n.locale ?? "en-US"
          } : undefined
        }
      }
    });
    $[0] = appearance;
    $[1] = authToken;
    $[2] = device;
    $[3] = l10n;
    $[4] = sandboxIdDocOutcome;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const generateQueryOptions = t1;
  let t2;
  if ($[6] !== generateQueryOptions) {
    t2 = {
      ...generateQueryOptions,
      gcTime: 0,
      refetchOnWindowFocus: false
    };
    $[6] = generateQueryOptions;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const generateQuery = useQuery(t2);
  const scopedAuthToken = generateQuery.data?.authToken;
  const t3 = scopedAuthToken ?? "";
  const t4 = !!scopedAuthToken;
  let t5;
  if ($[8] !== onCancel || $[9] !== onFinish) {
    t5 = status => {
      if (status === "completed" || status === "failed") {
        logInfo(`D2P session ${status}`);
        onFinish?.();
      } else {
        if (status === "canceled") {
          logInfo("D2P session canceled");
          onCancel?.();
        }
      }
    };
    $[8] = onCancel;
    $[9] = onFinish;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = {
      authToken: t3,
      enabled: t4,
      onStatusChange: t5
    };
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const d2pPoll = useD2pStatusPoll(t6);
  let t7;
  if ($[15] !== generateQuery || $[16] !== generateQueryOptions.queryKey || $[17] !== queryClient) {
    t7 = async () => {
      queryClient.removeQueries({
        queryKey: generateQueryOptions.queryKey
      });
      await generateQuery.refetch();
    };
    $[15] = generateQuery;
    $[16] = generateQueryOptions.queryKey;
    $[17] = queryClient;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const regenerate = t7;
  const handledPollError = useRef(null);
  let t8;
  let t9;
  if ($[19] !== d2pPoll.error || $[20] !== regenerate) {
    t8 = () => {
      if (!d2pPoll.error) {
        handledPollError.current = null;
        return;
      }
      if (handledPollError.current === d2pPoll.error) {
        return;
      }
      handledPollError.current = d2pPoll.error;
      logWarn("D2P session polling error, likely expired. Regenerating token.", d2pPoll.error);
      regenerate();
    };
    t9 = [d2pPoll.error, regenerate];
    $[19] = d2pPoll.error;
    $[20] = regenerate;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  const t10 = generateQuery.error ?? d2pPoll.error;
  let t11;
  if ($[23] !== d2pPoll.isCanceling || $[24] !== d2pPoll.isPolling || $[25] !== d2pPoll.status || $[26] !== generateQuery.isPending || $[27] !== scopedAuthToken || $[28] !== t10) {
    t11 = {
      scopedAuthToken,
      status: d2pPoll.status,
      isGenerating: generateQuery.isPending,
      isCanceling: d2pPoll.isCanceling,
      isPolling: d2pPoll.isPolling,
      error: t10
    };
    $[23] = d2pPoll.isCanceling;
    $[24] = d2pPoll.isPolling;
    $[25] = d2pPoll.status;
    $[26] = generateQuery.isPending;
    $[27] = scopedAuthToken;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== d2pPoll.cancel || $[31] !== regenerate || $[32] !== t11) {
    t12 = {
      state: t11,
      cancel: d2pPoll.cancel,
      regenerate
    };
    $[30] = d2pPoll.cancel;
    $[31] = regenerate;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
};
export default useD2pSession;